import React, { useState } from "react";
import Location from "../../assets/images/location.svg";
import Leash from "../../assets/images/leash.svg";
import Home from "../../assets/images/home.svg";
import Bowl from "../../assets/images/bowl.svg";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { useNavigate } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

type ServiceType = "boarding" | "dogWalking" | "houseSitting" | "dropIn" | null;

const DiscoverPetSitter: React.FC = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] =
    useState<ServiceType>("boarding");
  const [address, setAddress] = useState<string>("");
  const [frequency, setFrequency] = useState<"oneTime" | "repeatWeekly">(
    "oneTime",
  );
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("12:00 PM");
  const [endDate, setEndDate] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("12:00 PM");

  const services = [
    {
      id: "boarding" as ServiceType,
      icon: <img src={Location} alt="location" width="54px" height="54px" />,
      label: "Boarding",
    },
    {
      id: "dogWalking" as ServiceType,
      icon: <img src={Leash} alt="leash" width="54px" height="54px" />,
      label: "Dog Walking",
    },
    {
      id: "houseSitting" as ServiceType,
      icon: <img src={Home} alt="home" width="54px" height="54px" />,
      label: "House Sitting",
    },
    {
      id: "dropIn" as ServiceType,
      icon: <img src={Bowl} alt="bowl" width="54px" height="54px" />,
      label: "Drop-In Visits",
    },
  ];

  const handleServiceClick = (serviceId: ServiceType) => {
    setSelectedService(serviceId);
  };

  const handleNext = () => {
    console.log("Search params:", {
      service: selectedService,
      address,
      frequency:
        selectedService === "dogWalking" || selectedService === "dropIn"
          ? frequency
          : undefined,
      startDate,
      startTime,
      endDate,
      endTime,
    });

    //TODO: Pass search params to next page
    navigate("/dashboard");
  };

  // Check if current service needs frequency selector
  const needsFrequency =
    selectedService === "dogWalking" || selectedService === "dropIn";

  return (
    <Box sx={{ backgroundColor: "#FAF8F5", minHeight: "100vh", pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: 6 }}>
        {/* Title */}
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontSize: { xs: "2rem", md: "2.5rem" },
            fontWeight: 700,
            color: "#178B8E",
            mb: 6,
          }}
        >
          Discover Your Ideal Pet Sitter
        </Typography>

        {/* Service Selection Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {services.map((service) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={service.id}>
              <Card
                onClick={() => handleServiceClick(service.id)}
                sx={{
                  cursor: "pointer",
                  textAlign: { xs: "left", sm: "center" },
                  p: { xs: 1, sm: 2 },
                  border:
                    selectedService === service.id
                      ? "3px solid #FDB833"
                      : "3px solid transparent",
                  backgroundColor: "#FFFFFF",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", sm: "column" },
                    alignItems: "center",
                    gap: { xs: 2, sm: 0 },
                    p: { xs: "10px !important", sm: "16px" },
                    "&:last-child": { pb: { xs: "10px !important", sm: "24px" } },
                  }}
                >
                  <Box
                    sx={{
                      mb: { xs: 0, sm: 1 },
                      flexShrink: 0,
                      "& img": { width: { xs: 36, sm: 54 }, height: { xs: 36, sm: 54 } },
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#1A1A1A",
                      fontSize: { xs: "0.95rem", sm: "1.25rem" },
                    }}
                  >
                    {service.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Form Section */}
        {selectedService && (
          <Box sx={{ mt: 4 }}>
            {/* Address Input */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#1A1A1A", mb: 1 }}
              >
                Address
              </Typography>
              <TextField
                fullWidth
                placeholder="Address or Zip Code"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#FFFFFF",
                  },
                }}
              />
            </Box>
            {/* Frequency Selection (only for Dog Walking and Drop-In Visits) */}
            {needsFrequency && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1A1A1A", mb: 1 }}
                >
                  How often do you need?
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                      fullWidth
                      variant={
                        frequency === "oneTime" ? "contained" : "outlined"
                      }
                      onClick={() => setFrequency("oneTime")}
                      sx={{
                        py: 1.5,
                        backgroundColor:
                          frequency === "oneTime" ? "#FFFFFF" : "transparent",
                        color: "#5A5A5A",
                        border: "1px solid #E5E5E5",
                        "&:hover": {
                          backgroundColor:
                            frequency === "oneTime" ? "#F5F5F5" : "#FAF8F5",
                          border: "1px solid #E5E5E5",
                        },
                      }}
                    >
                      One Time
                    </Button>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                      fullWidth
                      variant={
                        frequency === "repeatWeekly" ? "contained" : "outlined"
                      }
                      onClick={() => setFrequency("repeatWeekly")}
                      sx={{
                        py: 1.5,
                        backgroundColor:
                          frequency === "repeatWeekly"
                            ? "#FFFFFF"
                            : "transparent",
                        color: "#5A5A5A",
                        border: "1px solid #E5E5E5",
                        "&:hover": {
                          backgroundColor:
                            frequency === "repeatWeekly"
                              ? "#F5F5F5"
                              : "#FAF8F5",
                          border: "1px solid #E5E5E5",
                        },
                      }}
                    >
                      Repeat Weekly
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
            {/* Wrap everything in LocalizationProvider if not already wrapped in parent */}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              width="100%"
              mb={2}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {/* Start Date & Time */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Start
                  </Typography>
                  <DateTimePicker
                    value={null}
                    onChange={(newValue: Dayjs | null) =>
                      console.log({ startDate: newValue?.toDate() ?? null })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        },
                      },
                    }}
                  />
                </Box>

                {/* End Date & Time */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    End
                  </Typography>
                  <DateTimePicker
                    value={null}
                    onChange={(newValue: Dayjs | null) =>
                      console.log({ endDate: newValue?.toDate() ?? null })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Stack>
            {/* Next Button */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
                sx={{
                  backgroundColor: "#FDB833",
                  color: "#1A1A1A",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  px: 8,
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: "#E0A42E",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default DiscoverPetSitter;
